import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronRight, X } from "lucide-react";
import { MarketingLayout } from "../components/MarketingLayout";
import { DashboardMockup } from "../components/DashboardMockup";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";
import {
  MODULE_GROUPS, BENEFITS, WORKFLOW_STEPS, PRODUCT_FACTS,
  TRADITIONAL_SETUP, ALEA_WAY, INDIA_COMPLIANCE, USE_CASES,
} from "../lib/content";

export default function Home() {
  useSEO({
    title: "Alea ERP — Business ERP Software for HR, Payroll, Finance & Operations",
    description:
      "Alea ERP is a business ERP that connects HR, payroll, finance, procurement, inventory and CRM in one platform. Indian statutory payroll built in, priced per organization — not per employee.",
    path: "/",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Alea ERP",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "INR" },
    },
  });

  return (
    <MarketingLayout>
      {/* ───────────────── Hero ───────────────── */}
      <section className="relative overflow-hidden bg-[#0a2d4a] pt-28 pb-24 sm:pt-32 lg:pt-40">
        {/* Dot grid + glow texture */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div aria-hidden="true" className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-full max-w-[820px] -translate-x-1/2 rounded-full bg-blue-500/20 blur-[120px]" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-xs font-medium text-blue-200 backdrop-blur"
            >
              <span aria-hidden="true" className="flex h-1.5 w-1.5 rounded-full bg-blue-400" />
              ERP built for Indian & growing businesses
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="mt-6 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
            >
              Your Entire Business.
              <br />
              <span className="bg-gradient-to-r from-blue-300 to-blue-500 bg-clip-text text-transparent">
                One ERP. One Price.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300"
            >
              Manage HR, payroll, finance, procurement, inventory, CRM and operations from
              one integrated platform. No disconnected tools. No per-employee pricing —
              just one plan for your whole organization.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link
                href="/contact"
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/40 transition-all hover:bg-blue-500 hover:shadow-blue-900/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
              >
                Book a Demo
                <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                href="/demo-access"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
              >
                Explore ERP
                <ArrowRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </motion.div>
          </div>

          <div className="lg:pl-4">
            <DashboardMockup />
          </div>
        </div>
      </section>

      {/* ───────────────── Verified product facts ───────────────── */}
      <section className="border-y border-slate-100 bg-white py-12">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid grid-cols-2 gap-y-8 gap-x-6 sm:grid-cols-4">
            {PRODUCT_FACTS.map((fact, i) => (
              <Reveal key={fact.label} delay={i * 0.06} className="flex flex-col items-center gap-2.5 text-center">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <fact.icon aria-hidden="true" className="h-5 w-5" />
                </span>
                <span className="text-sm font-semibold text-[#0a2d4a]">{fact.label}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Modules ───────────────── */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Everything in one place
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              Everything your business needs, connected in one place
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Replace a tangle of disconnected tools with one integrated platform that
              keeps every team working from the same source of truth.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {MODULE_GROUPS.map((group, gi) => (
              <Reveal key={group.category} delay={gi * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 sm:p-7">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-base font-bold uppercase tracking-wide text-blue-600">
                      {group.category}
                    </h3>
                    <span className="text-xs text-slate-400">{group.blurb}</span>
                  </div>
                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    {group.items.map((item) => (
                      <div key={item.title} className="flex gap-3">
                        <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                          <item.icon aria-hidden="true" className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                        </div>
                        <div>
                          <h4 className="text-sm font-bold text-[#0a2d4a]">{item.title}</h4>
                          <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-12 text-center">
            <Link
              href="/features"
              className="inline-flex items-center gap-1.5 rounded text-sm font-semibold text-blue-600 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              Explore all modules <ChevronRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── Why Alea ───────────────── */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              The difference
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              Why businesses choose Alea ERP
            </h2>
          </Reveal>

          <div className="mt-12 grid gap-5 sm:grid-cols-2">
            <Reveal className="rounded-2xl border border-slate-200 bg-slate-50/60 p-6 sm:p-7">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">
                Traditional setup
              </h3>
              <ul className="mt-4 space-y-3">
                {TRADITIONAL_SETUP.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <X aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.08} className="rounded-2xl border border-blue-200 bg-blue-50/50 p-6 sm:p-7">
              <h3 className="text-sm font-bold uppercase tracking-wide text-blue-700">
                Alea ERP
              </h3>
              <ul className="mt-4 space-y-3">
                {ALEA_WAY.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm font-medium text-[#0a2d4a]">
                    <Check aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ───────────────── Enterprise controls ───────────────── */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.3fr] lg:items-center">
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                Built for daily use
              </span>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
                Enterprise controls that pay off every day
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Security, access control and reporting aren't add-ons here — they're how
                every team already works in Alea ERP, so leadership can trust the system
                and teams can move fast.
              </p>
              <Link
                href="/about"
                className="mt-7 inline-flex items-center gap-1.5 rounded text-sm font-semibold text-blue-600 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              >
                Learn more about us <ChevronRight aria-hidden="true" className="h-4 w-4" />
              </Link>
            </Reveal>

            <div className="grid gap-4 sm:grid-cols-2">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={(i % 2) * 0.08}>
                  <div className="flex h-full gap-4 rounded-2xl border border-slate-100 bg-white p-5">
                    <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 shadow-sm">
                      <b.icon aria-hidden="true" className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#0a2d4a]">{b.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-500">{b.description}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Workflow ───────────────── */}
      <section className="relative overflow-hidden bg-[#0a2d4a] py-24">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-300">
              Connected workflows
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Every step tracked. Every decision recorded.
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              A single request moves through a configurable approval chain — nothing
              lost in email threads or spreadsheets.
            </p>
          </Reveal>

          <ol className="mt-16 flex flex-wrap items-center justify-center gap-3">
            {WORKFLOW_STEPS.map((step, i) => (
              <Reveal as="li" key={step.label} delay={i * 0.07} className="flex items-center gap-3">
                <div className="rounded-xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur">
                  {step.label}
                </div>
                {i < WORKFLOW_STEPS.length - 1 && (
                  <ArrowRight aria-hidden="true" className="hidden h-5 w-5 text-blue-400 sm:block" />
                )}
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ───────────────── India-ready ───────────────── */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] lg:items-center">
            <Reveal>
              <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
                Local compliance, built in
              </span>
              <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
                Built for Indian businesses
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Payroll and finance in Alea ERP are built around Indian statutory
                requirements from day one — not bolted on as an afterthought.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {INDIA_COMPLIANCE.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.05}>
                  <div className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                    <item.icon aria-hidden="true" className="h-4 w-4 shrink-0 text-blue-600" />
                    <span className="text-xs font-semibold text-[#0a2d4a]">{item.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────── Who is Alea for ───────────────── */}
      <section className="bg-slate-50 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Who it's for
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              Built for growing businesses
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {USE_CASES.map((u, i) => (
              <Reveal key={u.title} delay={i * 0.08}>
                <div className="h-full rounded-2xl border border-slate-200 bg-white p-6">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <u.icon aria-hidden="true" className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base font-bold text-[#0a2d4a]">{u.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{u.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────── Pricing connector ───────────────── */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-4xl px-5 text-center sm:px-8">
          <Reveal>
            <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">
              Simple pricing
            </span>
            <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[#0a2d4a] sm:text-4xl">
              One ERP. One predictable price.
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              No per-employee licensing. Choose a plan based on your organization size
              and get the modules your business needs.
            </p>
            <Link
              href="/pricing"
              className="mt-7 inline-flex items-center gap-1.5 rounded text-sm font-semibold text-blue-600 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              View Pricing <ChevronRight aria-hidden="true" className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ───────────────── Final CTA ───────────────── */}
      <section className="bg-white pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a2d4a] to-[#0d3a5f] px-8 py-16 text-center shadow-2xl sm:px-16">
              <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-blue-500/30 blur-3xl" />
              <h2 className="relative text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                Ready to bring your business onto one platform?
              </h2>
              <p className="relative mx-auto mt-4 max-w-xl text-lg text-slate-300">
                See how Alea connects HR, payroll, finance, operations and everything
                in between.
              </p>
              <div className="relative mt-9 flex flex-col justify-center gap-3 sm:flex-row">
                <Link
                  href="/contact"
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-8 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  Book a Demo
                  <ArrowRight aria-hidden="true" className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <Link
                  href="/pricing"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 px-8 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a2d4a]"
                >
                  Explore Pricing
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </MarketingLayout>
  );
}
